import { RHFAutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/RHFAutoCompleteVirtualize.tsx';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { useTargetingResetOnAccountChange } from '@openx/components-targeting/Targeting/shared/hooks/useTargetingResetOnAccountChange.tsx';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form.ts';
import { type DealFormDataWithTargeting, dealFormField } from '@openx/types/deal.ts';
import { produceSortedOptionsKeys } from '@openx/utils/lib/sortedOptions.ts';
import { type JSX, type SyntheticEvent, useCallback, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDealContext } from '../../utils/context/DealContext.tsx';
import { useDealFormContext } from '../DealForm.context.tsx';
import { getDealsLabels } from '../utils/dealFormUtils.ts';

type OnBehalfOfFormSectionProps = {
  isEdit: boolean;
};

export const OnBehalfOfFormSection = ({ isEdit }: OnBehalfOfFormSectionProps): JSX.Element => {
  const { useAccountOptionsFetch, setOnBehalfOrganization } = useDealContext();
  const { pckgIsInitialLoading } = useDealFormContext();
  const { resetField, setValue } = useFormContext<DealFormDataWithTargeting>();
  const { t } = useTranslation();

  const observableAccountUid = useWatch({ name: dealFormField.ACCOUNT_UID });

  const { accounts } = useAccountOptionsFetch();

  const dealsLabels = useMemo(() => getDealsLabels(t), [t]);

  const accountOptions = useMemo(() => produceSortedOptionsKeys(accounts.mapById), [accounts]);

  const { resetTargeting } = useTargetingResetOnAccountChange('deal');

  const handleOnManageAccountChange = useCallback(
    (_: SyntheticEvent, value: string) => {
      setValue(dealFormField.ACCOUNT_UID, value);
      setValue(dealFormField.CURRENCY, null);
      resetField('third_party_fees_config');
      resetField('third_party_fees_config_enabled');
      resetTargeting();

      const name = accounts.mapById[value].name;
      const account = accounts.list.find(account => account.name === name);

      if (account && setOnBehalfOrganization) {
        setOnBehalfOrganization(account);
      }
    },
    [setValue, accounts, setOnBehalfOrganization, resetField, resetTargeting]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: only run on render
  useEffect(() => {
    if (setOnBehalfOrganization) {
      setOnBehalfOrganization(null);
    }
  }, []);

  useEffect(() => {
    if (!setOnBehalfOrganization || !observableAccountUid || accounts.loading || !accounts.list?.length) {
      return;
    }

    const account = accounts.list.find(org => org.exchange_account_network?.uid === observableAccountUid);

    if (account) {
      setOnBehalfOrganization(account);
    }
  }, [observableAccountUid, accounts.loading, accounts.list, setOnBehalfOrganization]);

  return (
    <FormSection
      divider
      title={t('On Behalf Of')}
      description={t('Create deal on behalf of a different account.')}
      rightColumnSize={RIGHT_COLUMN_WIDTH}
    >
      <RHFAutoCompleteVirtualize
        name={dealFormField.ACCOUNT_UID}
        textFieldProps={{ label: dealsLabels[dealFormField.ACCOUNT_UID] }}
        disableClearable
        renderOptions={{
          dataTest: 'deal-account-item',
          tooltip: true,
        }}
        /** Account change resets targeting - it would be overwritten once the initial package targeting is applied */
        disabled={isEdit || !!pckgIsInitialLoading}
        getOptionLabel={option => accounts.mapById[option]?.name || option}
        options={accountOptions}
        onChange={handleOnManageAccountChange}
        data-test="deal-account"
      />
    </FormSection>
  );
};
